import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestorePageIcon from "@mui/icons-material/RestorePage";
import {
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DifferencePanelPopover } from "components/search/input/DifferencePanel/DifferencePanelPopover";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatLocalDateString } from "hooks/useFormatLocalDate";
import { MouseEventHandler } from "react";
import { SearchSummary } from "store/api/generatedApi";
import { SearchStatusChip } from "../common/SearchStatus";
import { useFormatVersionLabel } from "../common/versionLabel";
import { UserLabel } from "src/components/common/userLabel/UserLabel";

interface Props {
  versionNumber: number;
  search: SearchSummary;
  hasSearchChildren: boolean;
  currentSearchId: number;
  parentSearchId?: number;
  hover: boolean;
  onSelect: (search: SearchSummary) => void;
}

export const HistoryItemTitle = ({
  versionNumber,
  search,
  hasSearchChildren,
  currentSearchId,
  parentSearchId,
  hover,
  onSelect,
}: Props) => {
  const { t } = useTenantTranslation();
  const formatVersionLabel = useFormatVersionLabel();
  const theme = useTheme();

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect(search);
  };

  const isCurrentSearch = currentSearchId === search.search_id;

  const formattedCreatedAt = useFormatLocalDateString("datetime")(
    search.created_at
  );

  const content = (
    <AccordionSummary
      expandIcon={
        <ExpandMoreIcon
          sx={{ visibility: hasSearchChildren ? undefined : "hidden" }}
        />
      }
      sx={{
        flexDirection: "row-reverse",
        paddingLeft: 0,
        paddingRight: 0,
        alignItems: "flex-start",
        "& .MuiAccordionSummary-content": {
          marginTop: -0.5,
          marginBottom: 0.5,
          cursor: hasSearchChildren ? undefined : "default",
        },
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            ml: 1,
          }}
        >
          <Typography variant="h6" sx={{ py: 0.5 }}>
            {formatVersionLabel(versionNumber)}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }} gap={1}>
            <SearchStatusChip searchStatus={search.status} />
            {isCurrentSearch ? (
              <Chip label={t("currentlyViewing")} size="small" />
            ) : null}
          </Box>
          {hover ? (
            <Tooltip
              title={t("restoreThisSearchVersion")}
              placement="right"
              arrow
            >
              <IconButton
                sx={{ position: "absolute", right: 4 }}
                size="small"
                onClick={handleClick}
              >
                <RestorePageIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          <Typography
            variant="body2"
            sx={{ pl: 1, color: theme.palette.text.secondary }}
          >
            {formattedCreatedAt}
          </Typography>
          <UserLabel
            user={search.user}
            variant="body2"
            sx={{ pl: 1, color: theme.palette.text.secondary }}
          />
        </Box>
      </Box>
    </AccordionSummary>
  );

  if (parentSearchId) {
    return (
      <DifferencePanelPopover
        targetSearchId={search.search_id}
        sourceSearchId={parentSearchId}
      >
        {content}
      </DifferencePanelPopover>
    );
  }
  return content;
};
